import { withApollo } from '@apollo/client/react/hoc'
import BluebirdPromise from 'bluebird'
import _ from 'lodash'
import { PureComponent } from 'react'
import {
    ExclamationCircleOutlined,
    PaperClipOutlined,
    UploadOutlined,
} from '@ant-design/icons'
import { Modal, message, Spin, Input } from 'antd'
import { connect } from 'react-redux'

import { isQueryDetailsQueryRefactorOn } from '~/redux-modules/query-refactor/selector'
import { uploadItem } from '~/lib/uploadFile'
import logger from '~/lib/logger'
import getColor from '~/lib/getColor'
import Box from '~/core/Box'
import Button from '~/core/Button'
import Text from '~/core/Text'
import FileUploader from '~/core/FileUploader'
import { ACCEPTABLE_IM_UPLOAD_EXTS } from '~/lib/cadExtensions'
import { PROCESSID_3DP } from '~/lib/partsConstants'
import { FinalizeUploadAndProcessPartDocument } from '~/gql/graphql'

import {
    isCadFile,
    onProgress,
    uploadRevision,
    createQuoteAttachmentRevision,
    createQuoteModelRevisionAttachment,
    markQuoteAttachmentRevisionUploaded,
    imUploadRevisionWarning,
    genericUploadRevisionWarning,
} from './logic'
import { PLACEMENT, WAIT_TIME, MAX_LIMIT, PLACEHOLDER } from './constant'

class UploadQuoteModelRevisionModal extends PureComponent {
    state = {
        userAcceptedWarning: false,
        finishedClicked: false,
        revisionFileName: '',
        drawingFileName: '',
        enableDrawingUpload: false,
        _input: null,
        _resetInput: null,
        drawing: {
            type: '',
            quoteAttachmentRevisionId: '',
            uploadUrl: '',
            name: '',
        },
        complete: false,
        error: false,
        percent: 0,
        uploading: false,
        revisionNotes: '',
    }

    handleCancel = () => {
        this.setState({ userAcceptedWarning: false })
        this.resetParams()
        this.props.setUploadRevisionModalVisibility(false)
    }

    resetParams = () => {
        this.setState({
            userAcceptedWarning: false,
            finishedClicked: false,
            revisionFileName: '',
            drawingFileName: '',
            enableDrawingUpload: false,
            _input: null,
            _resetInput: null,
            complete: false,
            error: false,
            percent: 0,
            uploading: false,
        })
    }

    saveText = evt => {
        this.setState({
            revisionNotes: evt.target.value,
        })
    }

    onUploadComplete = data => {
        const uploadedFile = _.first(data)
        const { uploadMetadata } = uploadedFile

        if (!uploadMetadata?.quoteModelRevision?.id) {
            message.info(`Failed to upload file ${uploadedFile.name}.`)
            return this.handleCancel()
        }
        message.info(`${uploadedFile.name} has been uploaded successfully.`)

        return this.uploadDrawing(
            this.state.drawing,
            uploadMetadata.quoteModelRevision,
        )
    }

    userAcceptsRevisionMsg = () => {
        return this.setState({ userAcceptedWarning: true })
    }

    userClickedFinish = () => {
        return this.setState({ finishedClicked: true })
    }

    delayOrCallUpload = file => {
        return BluebirdPromise.delay(WAIT_TIME).then(() => {
            //this means the file has been cleared
            if (this.state.revisionFileName === '') {
                return false
            }
            return this.createEntity(file)
        })
    }

    createEntity = async file => {
        const {
            dispatch,
            quoteId,
            refetchQuery,
            manualRefetch,
            isQueryRefactorEnabled,
        } = this.props
        const { revisionNotes } = this.state

        const quoteModelId = this.props.quoteModelRevision.quoteModel.id
        const { units } = this.props.quoteModelRevision.partRevision

        if (!isCadFile(file)) {
            message.info('Please upload a CAD file')
            return false
        }

        this.setState({
            enableDrawingUpload: true,
            revisionFileName: file.name,
        })

        if (!this.state.finishedClicked) {
            return this.delayOrCallUpload(file)
        }
        const uploadRevisionParameter = {
            dispatch,
            file,
            quoteId,
            quoteModelId,
            units,
            revisionNotes,
            manualRefetch,
            isQueryRefactorEnabled,
        }

        if (this.state.drawingFileName === '') {
            uploadRevisionParameter['refetch'] = refetchQuery
        }

        const uploadSucceded = await uploadRevision(uploadRevisionParameter)

        if (!uploadSucceded) {
            this.handleCancel()
        }
    }

    uploadEntity = (...args) => {
        const [file, { onSuccess, onError }] = args

        if (!isCadFile(file)) {
            return false
        }

        return uploadItem(file, {
            onProgress: this.onProgress,
            onSuccess,
            onError,
        })
    }

    markUploadedEntity = async file => {
        const {
            client,
            refetchQuery,
            quoteId,
            manualRefetch,
            isQueryRefactorEnabled,
        } = this.props

        if (!isCadFile(file)) {
            return false
        }

        await client.mutate({
            mutation: FinalizeUploadAndProcessPartDocument,
            variables: {
                partId: file.uploadMetadata.quoteModelRevision.partRevision
                    .partId,
            },
            refetchQueries:
                this.state.revisionFileName &&
                this.state.drawingFileName === '' &&
                !isQueryRefactorEnabled &&
                refetchQuery,
        })
        if (isQueryRefactorEnabled && manualRefetch) {
            manualRefetch(quoteId)
        }
    }

    onProgress = e => {
        this.setState(onProgress(e))
    }

    onInputChange = e => {
        e.preventDefault()
        const [file] = _.toArray(
            e.dataTransfer ? e.dataTransfer.files : e.target.files,
        )

        return this.setState({ drawing: file, drawingFileName: file.name })
    }

    onDrawingUploadSuccess = async ({
        file,
        quoteAttachmentRevision,
        refetchQuery,
        newQuoteModelRevision,
    }) => {
        const { dispatch, quoteId, manualRefetch, isQueryRefactorEnabled } =
            this.props

        await dispatch(
            markQuoteAttachmentRevisionUploaded({
                file,
                quoteId,
                refetchQuery,
                manualRefetch,
                isQueryRefactorEnabled,
            }),
        ).catch(error => {
            logger.exception(error, {
                extra: {
                    info: 'Failed to mark attachment uploaded',
                },
            })
            message.error(`${file.name} failed to complete the upload`)
        })

        return dispatch(
            createQuoteModelRevisionAttachment({
                quoteModelRevisionId: _.get(newQuoteModelRevision, 'id'),
                quoteAttachmentRevisionId: quoteAttachmentRevision.id,
            }),
        )
            .then(() => {
                message.success('Drawing attached.')

                return this.allUploadsCompleted()
            })
            .catch(error => {
                logger.exception(error)
                message.error('Failed to link drawing.')
            })
    }

    uploadDrawing = async (file, newQuoteModelRevision) => {
        const { dispatch, quoteId, refetchQuery } = this.props

        if (this.state.drawingFileName !== '' && newQuoteModelRevision) {
            const quoteAttachmentRevision = await dispatch(
                createQuoteAttachmentRevision({
                    file,
                    quoteId,
                    attachmentType: null,
                    quoteAttachmentId: null,
                    productAttachmentId: null,
                }),
            )

            const _file = file

            _file.quoteAttachmentRevisionId = quoteAttachmentRevision.id
            _file.uploadUrl =
                quoteAttachmentRevision.attachment.uploadUrl ||
                quoteAttachmentRevision.asset.uploadUrl

            return uploadItem(_file, {
                onProgress: this.onProgress,
                onSuccess: () => {
                    this.onDrawingUploadSuccess({
                        file: _file,
                        quoteAttachmentRevision,
                        refetchQuery,
                        newQuoteModelRevision,
                    })
                },
                onError: error => {
                    message.error(error)
                },
            })
        }

        this.allUploadsCompleted()
        return false
    }

    allUploadsCompleted = () => {
        const {
            props: { onClose },
        } = this

        this.handleCancel()
        this.props.refetch?.()

        return onClose?.()
    }

    showUploadButton = () => {
        return (
            <Button
                name="select-revision-for-upload"
                placement={PLACEMENT}
                disabled={this.state.revisionFileName !== ''}
            >
                <UploadOutlined /> Click to upload
            </Button>
        )
    }

    onClickDrawingUpload = () => {
        if (this.state._input) {
            this.state._input.click()
        }
    }

    onRefChange = _input => {
        this.setState({ _input })
    }

    render() {
        const {
            quoteModelRevision: { modelConfiguration },
        } = this.props

        const acceptedFiles =
            modelConfiguration &&
            modelConfiguration?.process['id'] !== PROCESSID_3DP
                ? ACCEPTABLE_IM_UPLOAD_EXTS
                : null

        //Enzyme is not supporting destructuring of jsx component out side of body of function component that's why changes form <TextArea to <Input.TextArea.
        if (this.state.userAcceptedWarning) {
            return (
                <Modal
                    title="Upload part revision"
                    footer={
                        <span>
                            {this.state.finishedClicked && (
                                <Spin style={{ paddingRight: '5px' }} />
                            )}
                            <Button
                                name="change-configuration"
                                placement={PLACEMENT}
                                onClick={this.userClickedFinish}
                                type="primary"
                                disabled={!this.state.enableDrawingUpload}
                            >
                                {this.state.finishedClicked ? (
                                    <span>Uploading</span>
                                ) : (
                                    'Finish'
                                )}
                            </Button>
                        </span>
                    }
                    visible={this.props.visible}
                    onCancel={this.handleCancel}
                    width="560px"
                >
                    <Box
                        direction="col"
                        justifyContent="start"
                        style={{ fontSize: '14px' }}
                    >
                        <Box direction="col" justifyContent="start">
                            <Box justifyContent="start">
                                <span style={{ color: getColor('error') }}>
                                    *
                                </span>{' '}
                                Upload part
                            </Box>
                            <Box direction="row">
                                <FileUploader
                                    accept={acceptedFiles}
                                    placement={PLACEMENT}
                                    onFileDropped={this.createEntity}
                                    onUpload={this.uploadEntity}
                                    onUploadSuccess={this.markUploadedEntity}
                                    onSuccess={this.onUploadComplete}
                                    showUploadList={false}
                                    buttonType="primary"
                                    customSpinner={this.showUploadButton()}
                                    allowOnlyOneFile
                                >
                                    {this.showUploadButton()}
                                </FileUploader>
                                <Box
                                    alignItems="end"
                                    marginLeft="20px"
                                    style={{
                                        color: getColor('grayDark'),
                                    }}
                                >
                                    {this.state.revisionFileName && (
                                        <Box alignItems="center">
                                            <PaperClipOutlined
                                                style={{ marginRight: '5px' }}
                                            />
                                            {this.state.revisionFileName}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                        <Box direction="col" marginTop="20px">
                            {!this.props.isPrimaryProcess3dp && (
                                <Box direction="col">
                                    <Box>Upload drawing (optional)</Box>
                                    <Box>
                                        <Button
                                            name="select-drawing-for-upload"
                                            placement={PLACEMENT}
                                            onClick={this.onClickDrawingUpload}
                                            disabled={
                                                !this.state
                                                    .enableDrawingUpload ||
                                                this.state.drawingFileName !==
                                                    ''
                                            }
                                        >
                                            <UploadOutlined /> Click to upload
                                        </Button>
                                        <input
                                            ref={this.onRefChange}
                                            accept={'application/pdf'}
                                            multiple={false}
                                            onChange={this.onInputChange}
                                            style={{ display: 'none' }}
                                            type="file"
                                        />
                                        <Box
                                            alignItems="end"
                                            marginLeft="20px"
                                            style={{
                                                color: getColor('grayDark'),
                                            }}
                                        >
                                            {this.state.drawingFileName && (
                                                <Box>
                                                    <PaperClipOutlined
                                                        style={{
                                                            paddingRight: '5px',
                                                        }}
                                                    />
                                                    {this.state.drawingFileName}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                            <Box marginTop="20px">
                                Notes to describe this revision (optional)
                            </Box>
                            <Input.TextArea
                                date-test-target={`${PLACEMENT}-notes`}
                                maxLength={MAX_LIMIT}
                                placeholder={PLACEHOLDER}
                                marginLeft="12px"
                                style={{
                                    height: '60px',
                                    top: '2.5px',
                                    Width: '464px',
                                }}
                                name={`${PLACEMENT}-notes`}
                                value={this.state.revisionNotes}
                                onChange={this.saveText}
                            />
                            <Box
                                paddingTop="10px"
                                justifyContent="end"
                                color={getColor('blackAlpha25')}
                            >
                                {`${MAX_LIMIT} characters max.`}
                            </Box>
                        </Box>
                    </Box>
                </Modal>
            )
        }

        return (
            <Modal
                title={null}
                footer={null}
                visible={this.props.visible}
                onCancel={this.handleCancel}
                width="560px"
            >
                <Box direction="col">
                    <Box style={{ fontSize: '16px' }} alignItems="center">
                        <ExclamationCircleOutlined
                            style={{
                                color: getColor('warning'),
                                marginRight: '8px',
                            }}
                        />{' '}
                        <Text fontWeight={500}>
                            Heads-up before you upload part revisions:
                        </Text>
                    </Box>
                    <Box
                        padding="20px 35px 0px 20px"
                        justifyContent="center"
                        alignItems="center"
                    >
                        {this.props.isImQuote
                            ? imUploadRevisionWarning
                            : genericUploadRevisionWarning}
                    </Box>
                    <Box marginTop="25px" justifyContent="end">
                        <Button
                            name="change-configuration"
                            placement={PLACEMENT}
                            onClick={this.userAcceptsRevisionMsg}
                            type="primary"
                        >
                            I understand
                        </Button>
                    </Box>
                </Box>
            </Modal>
        )
    }
}

export default _.flowRight(
    connect(state => {
        const isQueryRefactorEnabled = isQueryDetailsQueryRefactorOn(state)

        return {
            isQueryRefactorEnabled,
        }
    }),
    withApollo,
)(UploadQuoteModelRevisionModal)
